import enBase from "./locales/en.json";
import deBase from "./locales/de.json";
import enPmsBilling from "../languages/en/en_pms_billing";
import enPmsChannelManager from "../languages/en/en_pms_channel_manager";
import enPmsCheckinStay from "../languages/en/en_pms_checkin_stay";
import enPmsCommon from "../languages/en/en_pms_common";
import enPmsGroups from "../languages/en/en_pms_groups";
import enPmsGuests from "../languages/en/en_pms_guests"; 
import enPmsHousekeeping from "../languages/en/en_pms_housekeeping";
import enPmsLiveBoard from "../languages/en/en_pms_live_board";
import enPmsManagerDashboard from "../languages/en/en_pms_manager_dashboard";
import enPmsNightAudit from "../languages/en/en_pms_night_audit";
import enPmsPersonalDashboard from "../languages/en/en_pms_personal_dashboard";
import enPmsReservations from "../languages/en/en_pms_reservations";
import enPmsRevenue from "../languages/en/en_pms_revenue";
import enPmsReports from "../languages/en/en_pms_reports";
import enPos from "../languages/en/en_pos";
import dePmsBilling from "../languages/de/de_pms_billing";
import dePmsChannelManager from "../languages/de/de_pms_channel_manager";
import dePmsCheckinStay from "../languages/de/de_pms_checkin_stay";
import dePmsCommon from "../languages/de/de_pms_common";
import dePmsGroups from "../languages/de/de_pms_groups"; 
import dePmsGuests from "../languages/de/de_pms_guests";
import dePmsHousekeeping from "../languages/de/de_pms_housekeeping";
import dePmsLiveBoard from "../languages/de/de_pms_live_board";
import dePmsManagerDashboard from "../languages/de/de_pms_manager_dashboard";
import dePmsNightAudit from "../languages/de/de_pms_night_audit";
import dePmsPersonalDashboard from "../languages/de/de_pms_personal_dashboard";
import dePmsReservations from "../languages/de/de_pms_reservations";
import dePmsRevenue from "../languages/de/de_pms_revenue";
import dePmsReports from "../languages/de/de_pms_reports";
import dePos from "../languages/de/de_pos";
import plPmsBilling from "../languages/pl/pl_pms_billing";
import plPmsChannelManager from "../languages/pl/pl_pms_channel_manager";
import plPmsCheckinStay from "../languages/pl/pl_pms_checkin_stay";
import plPmsCommon from "../languages/pl/pl_pms_common";
import plPmsGroups from "../languages/pl/pl_pms_groups";
import plPmsGuests from "../languages/pl/pl_pms_guests";
import plPmsHousekeeping from "../languages/pl/pl_pms_housekeeping";
import plPmsLiveBoard from "../languages/pl/pl_pms_live_board";
import plPmsManagerDashboard from "../languages/pl/pl_pms_manager_dashboard";
import plPmsNightAudit from "../languages/pl/pl_pms_night_audit";
import plPmsPersonalDashboard from "../languages/pl/pl_pms_personal_dashboard";
import plPmsReservations from "../languages/pl/pl_pms_reservations";
import plPmsRevenue from "../languages/pl/pl_pms_revenue";
import plPmsReports from "../languages/pl/pl_pms_reports";
import plPos from "../languages/pl/pl_pos";
import frPmsBilling from "../languages/fr/fr_pms_billing";
import frPmsChannelManager from "../languages/fr/fr_pms_channel_manager";
import frPmsCheckinStay from "../languages/fr/fr_pms_checkin_stay";
import frPmsCommon from "../languages/fr/fr_pms_common";
import frPmsGroups from "../languages/fr/fr_pms_groups";
import frPmsGuests from "../languages/fr/fr_pms_guests";
import frPmsHousekeeping from "../languages/fr/fr_pms_housekeeping";
import frPmsLiveBoard from "../languages/fr/fr_pms_live_board";
import frPmsManagerDashboard from "../languages/fr/fr_pms_manager_dashboard";
import frPmsNightAudit from "../languages/fr/fr_pms_night_audit";
import frPmsPersonalDashboard from "../languages/fr/fr_pms_personal_dashboard";
import frPmsReservations from "../languages/fr/fr_pms_reservations";
import frPmsRevenue from "../languages/fr/fr_pms_revenue";
import frPmsReports from "../languages/fr/fr_pms_reports";

type TranslationTree = Record<string, unknown>;

function isPlainObject(value: unknown): value is TranslationTree {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function deepMerge<T extends TranslationTree>(base: T, ...overrides: TranslationTree[]): T {
  const output: TranslationTree = { ...base };

  for (const override of overrides) {
    for (const [key, value] of Object.entries(override)) {
      const current = output[key];
      if (isPlainObject(current) && isPlainObject(value)) {
        output[key] = deepMerge(current, value);
        continue;
      }
      output[key] = value;
    }
  }

  return output as T;
}

function buildPmsResource(parts: {
  common: TranslationTree;
  billing: TranslationTree;
  channelManager: TranslationTree;
  checkinStay: TranslationTree;
  groups: TranslationTree;
  guests: TranslationTree;
  housekeeping: TranslationTree;
  liveBoard: TranslationTree;
  managerDashboard: TranslationTree;
  nightAudit: TranslationTree;
  personalDashboard: TranslationTree;
  reservations: TranslationTree;
  revenue: TranslationTree;
  reports: TranslationTree;
}) {
  return {
    pms: {
      common: parts.common,
      billing: parts.billing,
      channelManager: parts.channelManager,
      checkinStay: parts.checkinStay,
      groups: parts.groups,
      guests: parts.guests,
      housekeeping: parts.housekeeping,
      liveBoard: parts.liveBoard,
      managerDashboard: parts.managerDashboard,
      nightAudit: parts.nightAudit,
      personalDashboard: parts.personalDashboard,
      reservations: parts.reservations,
      revenue: parts.revenue,
      reports: parts.reports,
    },
  };
}

const sharedEnglish = {
  language: {
    de: "German",
    en: "English",
    pl: "Polish",
    fr: "French",
  },
};

const sharedGerman = {
  language: {
    de: "Deutsch",
    en: "Englisch",
    pl: "Polnisch",
    fr: "Franzoesisch",
  },
};

const sharedPolish = {
  language: {
    de: "Niemiecki",
    en: "Angielski",
    pl: "Polski",
    fr: "Francuski",
  },
  identity: {
    all_properties: "Wszystkie obiekty",
    property: "Obiekt",
  },
  auth: {
    welcome_back: "Witamy ponownie",
    sign_in_subtitle: "Zaloguj się przy użyciu danych administratora, aby kontynuować.",
    username: "Nazwa użytkownika",
    password: "Hasło",
    login_placeholder: "Login",
    login: "Zaloguj się",
    change_language: "Zmień język",
    show_password: "Pokaż hasło",
    hide_password: "Ukryj hasło",
    signing_in: "Logowanie...",
    invalid: "Nieprawidłowe dane logowania lub kod.",
  },
  hos_dashboard: {
    alerts: {
      title: "Alerty",
      empty: "Wszystko w porządku — obecnie brak alertów",
      view_all: "Wyświetl wszystkie alerty ({{count}}) ›",
      dialog_label: "Wszystkie alerty",
      count_one: "{{count}} alert",
      count_few: "{{count}} alerty",
      count_many: "{{count}} alertów",
      count_other: "{{count}} alertu",
      close: "Zamknij",
    },
  },
};

const sharedFrench = {
  language: {
    de: "Allemand",
    en: "Anglais",
    pl: "Polonais",
    fr: "Francais",
  },
  identity: {
    all_properties: "Tous les établissements",
    property: "Établissement",
  },
  auth: {
    welcome_back: "Bienvenue",
    sign_in_subtitle: "Connectez-vous avec vos identifiants administrateur pour continuer.",
    username: "Nom d’utilisateur",
    password: "Mot de passe",
    login_placeholder: "Identifiant",
    login: "Connexion",
    change_language: "Changer de langue",
    show_password: "Afficher le mot de passe",
    hide_password: "Masquer le mot de passe",
    signing_in: "Connexion en cours...",
    invalid: "Identifiants ou code incorrects.",
  },
  hos_dashboard: {
    alerts: {
      title: "Alertes",
      empty: "Tout est en ordre — aucune alerte actuellement",
      view_all: "Afficher les {{count}} alertes ›",
      dialog_label: "Toutes les alertes",
      count_one: "{{count}} alerte",
      count_other: "{{count}} alertes",
      close: "Fermer",
    },
  },
};

export const translationResources = {
  en: {
    translation: deepMerge(
      enBase as TranslationTree,
      sharedEnglish,
      { pos: enPos },
      buildPmsResource({
        common: enPmsCommon,
        billing: enPmsBilling,
        channelManager: enPmsChannelManager,
        checkinStay: enPmsCheckinStay,
        groups: enPmsGroups,
        guests: enPmsGuests,
        housekeeping: enPmsHousekeeping,
        liveBoard: enPmsLiveBoard,
        managerDashboard: enPmsManagerDashboard,
        nightAudit: enPmsNightAudit,
        personalDashboard: enPmsPersonalDashboard,
        reservations: enPmsReservations,
        revenue: enPmsRevenue,
        reports: enPmsReports,
      }),
    ),
  },
  de: {
    translation: deepMerge(
      deBase as TranslationTree,
      sharedGerman,
      { pos: dePos },
      buildPmsResource({
        common: dePmsCommon,
        billing: dePmsBilling,
        channelManager: dePmsChannelManager,
        checkinStay: dePmsCheckinStay,
        groups: dePmsGroups,
        guests: dePmsGuests,
        housekeeping: dePmsHousekeeping,
        liveBoard: dePmsLiveBoard,
        managerDashboard: dePmsManagerDashboard,
        nightAudit: dePmsNightAudit,
        personalDashboard: dePmsPersonalDashboard,
        reservations: dePmsReservations,
        revenue: dePmsRevenue,
        reports: dePmsReports,
      }),
    ),
  },
  pl: {
    translation: deepMerge(
      enBase as TranslationTree,
      sharedPolish,
      { pos: plPos },
      buildPmsResource({
        common: plPmsCommon,
        billing: plPmsBilling,
        channelManager: plPmsChannelManager,
        checkinStay: plPmsCheckinStay,
        groups: plPmsGroups,
        guests: plPmsGuests,
        housekeeping: plPmsHousekeeping,
        liveBoard: plPmsLiveBoard,
        managerDashboard: plPmsManagerDashboard,
        nightAudit: plPmsNightAudit,
        personalDashboard: plPmsPersonalDashboard,
        reservations: plPmsReservations,
        revenue: plPmsRevenue,
        reports: plPmsReports,
      }),
    ),
  },
  fr: {
    translation: deepMerge(
      enBase as TranslationTree,
      sharedFrench,
      { pos: enPos },
      buildPmsResource({
        common: frPmsCommon,
        billing: frPmsBilling,
        channelManager: frPmsChannelManager,
        checkinStay: frPmsCheckinStay,
        groups: frPmsGroups,
        guests: frPmsGuests,
        housekeeping: frPmsHousekeeping,
        liveBoard: frPmsLiveBoard,
        managerDashboard: frPmsManagerDashboard,
        nightAudit: frPmsNightAudit,
        personalDashboard: frPmsPersonalDashboard,
        reservations: frPmsReservations,
        revenue: frPmsRevenue,
        reports: frPmsReports,
      }),
    ),
  },
} as const;
